// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — statistics a paper can use (`docs/AYNI_XR_RESEARCH_TIER_PLAN.md`,
 * §Next). The owner asked (2026-10-06) that "the math and scientific use
 * are sound". Every function names its source; where a published method
 * is known to over-state certainty for the series this app reads, the
 * code says so and the app does not lean on it alone.
 *
 * Reviewed 2026-10-06 with Monte-Carlo checks (AR(1) nulls, 1,000–3,000
 * replicates). What follows from that review:
 *
 * - **Least squares** with an AR(1) effective sample size and the Nychka
 *   et al. (2000) small-sample adjustment,
 *   n_eff = n(1 − r₁ − 0.68/√n)/(1 + r₁ + 0.68/√n): correct size (≈ 5 %)
 *   from n = 30. Santer et al. (2000, JGR 105:7337) widening of the
 *   standard error, df = n_eff − 2.
 * - **Mann–Kendall** with Kendall's tie correction and the Hamed & Rao
 *   (1998) variance correction (clamped ≥ 1). Hamed–Rao is the standard
 *   method (pymannkendall implements it the same way), but detrending
 *   before the correction makes it liberal for persistent series: 16–23 %
 *   false trends at a nominal 5 % for AR(1) φ = 0.5–0.8. So the app calls
 *   a trend significant only when least squares agrees.
 * - **Sen's slope** (Sen 1968): the median of pairwise slopes, robust to
 *   outliers. Its Gilbert (1987) interval is computed with the corrected
 *   Mann–Kendall variance and shares that test's optimism, so the app
 *   quotes the least-squares interval and Sen's slope as the robust point.
 * - **Correlation** (Pearson, Spearman) with the Bretherton et al. (1999)
 *   n_eff = n(1 − r₁ₓr₁ᵧ)/(1 + r₁ₓr₁ᵧ): size 4.6–5.5 % in simulation.
 * - **Time of emergence** (Hawkins & Sutton 2012): noise is the standard
 *   deviation of the detrended baseline period (or a value the caller
 *   brings, from observations or one model run: an ensemble median is far
 *   smoother than any real climate); signal is a centred running mean
 *   measured in time; emergence must last to the end of the record.
 * - Lag-1 autocorrelation over gaps is normalised to the pairs present,
 *   so a gap never makes a series look less persistent than it is.
 *
 * Pure: no DOM, no app state.
 */

// ------------------------------------------------------------------ distributions

/** erfc(x), fractional error under 1.2e-7 everywhere (Numerical Recipes, `erfcc`). */
export function erfc(x: number): number {
  const z = Math.abs(x)
  const t = 1 / (1 + 0.5 * z)
  const r = t * Math.exp(-z * z - 1.26551223 + t * (1.00002368 + t * (0.37409196 + t * (0.09678418 +
    t * (-0.18628806 + t * (0.27886807 + t * (-1.13520398 + t * (1.48851587 + t * (-0.82215223 + t * 0.17087277)))))))))
  return x >= 0 ? r : 2 - r
}

/** Φ(z), the standard normal CDF (relative error under 1.2e-7). */
export function normalCdf(z: number): number {
  return 0.5 * erfc(-z / Math.SQRT2)
}

/** Two-sided normal p-value, without the cancellation of 2(1 − Φ(|z|)) for large |z|. */
export function normalTwoSidedP(z: number): number {
  return erfc(Math.abs(z) / Math.SQRT2)
}

/** Φ⁻¹(p) by Acklam's algorithm, relative error under 1.2e-9. */
export function normalQuantile(p: number): number {
  if (!(p > 0 && p < 1)) return p === 0 ? -Infinity : p === 1 ? Infinity : NaN
  const a = [-39.69683028665376, 220.9460984245205, -275.9285104469687, 138.357751867269, -30.66479806614716, 2.506628277459239]
  const b = [-54.47609879822406, 161.5858368580409, -155.6989798598866, 66.80131188771972, -13.28068155288572]
  const c = [-0.007784894002430293, -0.3223964580411365, -2.400758277161838, -2.549732539343734, 4.374664141464968, 2.938163982698783]
  const d = [0.007784695709041462, 0.3224671290700398, 2.445134137142996, 3.754408661907416]
  const lo = 0.02425
  if (p < lo) {
    const q = Math.sqrt(-2 * Math.log(p))
    return (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) / ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
  }
  if (p <= 1 - lo) {
    const q = p - 0.5
    const r = q * q
    return ((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q) / (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1)
  }
  const q = Math.sqrt(-2 * Math.log(1 - p))
  return -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) / ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
}

/** ln Γ(x), Lanczos approximation (g = 7, n = 9), accurate to about 1e-15. */
export function lnGamma(x: number): number {
  const g = 7
  const coef = [0.99999999999980993, 676.5203681218851, -1259.1392167224028, 771.32342877765313, -176.61502916214059,
    12.507343278686905, -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7]
  if (x < 0.5) return Math.log(Math.PI / Math.sin(Math.PI * x)) - lnGamma(1 - x)
  x -= 1
  let a = coef[0]
  const t = x + g + 0.5
  for (let i = 1; i < g + 2; i++) a += coef[i] / (x + i)
  return 0.5 * Math.log(2 * Math.PI) + (x + 0.5) * Math.log(t) - t + Math.log(a)
}

/** The regularised incomplete beta function I_x(a, b) (continued fraction, Numerical Recipes `betai`). */
export function incompleteBeta(x: number, a: number, b: number): number {
  if (x <= 0) return 0
  if (x >= 1) return 1
  const front = Math.exp(lnGamma(a + b) - lnGamma(a) - lnGamma(b) + a * Math.log(x) + b * Math.log(1 - x))
  const cf = (xx: number, aa: number, bb: number): number => {
    const fpmin = 1e-300
    let c = 1
    let d = 1 - ((aa + bb) * xx) / (aa + 1)
    if (Math.abs(d) < fpmin) d = fpmin
    d = 1 / d
    let h = d
    for (let m = 1; m <= 300; m++) {
      const m2 = 2 * m
      let aa2 = (m * (bb - m) * xx) / ((aa + m2 - 1) * (aa + m2))
      d = 1 + aa2 * d
      if (Math.abs(d) < fpmin) d = fpmin
      c = 1 + aa2 / c
      if (Math.abs(c) < fpmin) c = fpmin
      d = 1 / d
      h *= d * c
      aa2 = (-(aa + m) * (aa + bb + m) * xx) / ((aa + m2) * (aa + m2 + 1))
      d = 1 + aa2 * d
      if (Math.abs(d) < fpmin) d = fpmin
      c = 1 + aa2 / c
      if (Math.abs(c) < fpmin) c = fpmin
      d = 1 / d
      const del = d * c
      h *= del
      if (Math.abs(del - 1) < 1e-14) break
    }
    return h
  }
  return x < (a + 1) / (a + b + 2) ? (front * cf(x, a, b)) / a : 1 - (front * cf(1 - x, b, a)) / b
}

/** Student's t CDF with `df` degrees of freedom (df may be fractional, as n_eff − 2 is). */
export function studentTCdf(t: number, df: number): number {
  if (!(df > 0)) return NaN
  if (!Number.isFinite(t)) return t > 0 ? 1 : 0
  const tail = 0.5 * incompleteBeta(df / (df + t * t), df / 2, 0.5)
  return t >= 0 ? 1 - tail : tail
}

/** The t value with CDF p (bisection on the exact CDF; to 1e-10). */
export function studentTQuantile(p: number, df: number): number {
  if (!(p > 0 && p < 1) || !(df > 0)) return NaN
  let lo = -1e4
  let hi = 1e4
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2
    if (studentTCdf(mid, df) < p) lo = mid
    else hi = mid
    if (hi - lo < 1e-12) break
  }
  return (lo + hi) / 2
}

/** Two-sided p-value of a t statistic, straight from the incomplete beta (no cancellation for small p). */
export function tTestP(t: number, df: number): number {
  if (!(df > 0)) return NaN
  if (!Number.isFinite(t)) return 0
  return incompleteBeta(df / (df + t * t), df / 2, 0.5)
}

// ------------------------------------------------------------------ helpers

/** Average ranks, 1-based, ties sharing their mean rank. */
export function ranks(values: readonly number[]): number[] {
  const order = values.map((v, i) => [v, i] as const).sort((p, q) => p[0] - q[0])
  const out = new Array<number>(values.length)
  for (let i = 0; i < order.length;) {
    let j = i
    while (j + 1 < order.length && order[j + 1][0] === order[i][0]) j++
    const rank = (i + j) / 2 + 1
    for (let k = i; k <= j; k++) out[order[k][1]] = rank
    i = j + 1
  }
  return out
}

function median(sorted: readonly number[]): number {
  const n = sorted.length
  if (n === 0) return NaN
  return n % 2 ? sorted[(n - 1) / 2] : (sorted[n / 2 - 1] + sorted[n / 2]) / 2
}

/** The order statistic at a 1-based, possibly fractional, position (linear between neighbours; clamped). */
function orderStatistic(sorted: readonly number[], position: number): number {
  const p = Math.min(sorted.length, Math.max(1, position))
  const lo = Math.floor(p)
  const f = p - lo
  return f === 0 || lo >= sorted.length ? sorted[lo - 1] : sorted[lo - 1] + f * (sorted[lo] - sorted[lo - 1])
}

/**
 * Autocorrelation at `lag` steps of a sequence on a regular grid with
 * gaps. `grid[i]` is point i's integer position (default: its index);
 * only pairs exactly `lag` apart count, and their sum is scaled to the
 * number of pairs a gap-free series would have, so gaps do not shrink the
 * estimate (the standard estimator, Σ over n − lag pairs / Σ over n
 * squares, is unchanged without gaps).
 */
export function autocorrelation(values: readonly number[], lag: number, grid?: readonly number[]): number {
  const n = values.length
  if (n < 3 || lag < 1) return 0
  const mean = values.reduce((s, v) => s + v, 0) / n
  let den = 0
  for (const v of values) den += (v - mean) ** 2
  if (!(den > 0)) return 0
  const pos = grid ?? values.map((_, i) => i)
  const index = new Map<number, number>()
  pos.forEach((p, i) => index.set(p, i))
  let num = 0
  let pairs = 0
  for (let i = 0; i < n; i++) {
    const j = index.get(pos[i] + lag)
    if (j === undefined) continue
    num += (values[i] - mean) * (values[j] - mean)
    pairs++
  }
  if (pairs === 0) return 0
  // Pairs a gap-free series of the same length would have.
  const expected = Math.max(1, n - lag)
  return ((num * expected) / pairs) / den
}

/** Lag-1 autocorrelation; `adjacent[i]` says point i+1 is the next step (no gap). */
export function lag1Autocorrelation(values: readonly number[], adjacent?: readonly boolean[]): number {
  if (!adjacent) return autocorrelation(values, 1)
  // Rebuild grid positions from the adjacency: a gap adds at least one step.
  const grid: number[] = [0]
  for (let i = 1; i < values.length; i++) grid.push(grid[i - 1] + (adjacent[i - 1] ? 1 : 2))
  return autocorrelation(values, 1, grid)
}

/** n_eff for an AR(1) process with lag-1 autocorrelation r (Bretherton; only positive r reduces n). */
export function effectiveN(n: number, r1: number): number {
  if (!(r1 > 0)) return n
  return Math.max(0, Math.min(n, (n * (1 - r1)) / (1 + r1)))
}

/**
 * n_eff for a least-squares trend test with the Nychka et al. (2000)
 * small-sample adjustment of the residuals' r₁ (biased low in short
 * series): n(1 − r₁ − 0.68/√n)/(1 + r₁ + 0.68/√n).
 */
export function effectiveNNychka(n: number, r1: number): number {
  const k = 0.68 / Math.sqrt(n)
  const r = Math.max(0, r1)
  return Math.max(0, Math.min(n, (n * (1 - r - k)) / (1 + r + k)))
}

// ------------------------------------------------------------------ trend

/** Below this many independent values a result is reported, flagged "interpret with caution". */
export const LOW_INDEPENDENT = 10

export interface OlsTrend {
  readonly n: number
  readonly slope: number
  readonly intercept: number
  readonly r2: number
  /** Lag-1 autocorrelation of the residuals. */
  readonly lag1: number
  readonly nEff: number
  /** Degrees of freedom of the test: n_eff − 2. */
  readonly df: number
  /** Standard error of the slope, widened for autocorrelation. */
  readonly seSlope: number
  readonly ciLow: number
  readonly ciHigh: number
  readonly pValue: number
  /** Three or fewer independent points: the slope stands, the test does not. */
  readonly tooFewIndependent: boolean
  /** Fewer than {@link LOW_INDEPENDENT} independent points: interpret with caution. */
  readonly lowIndependent: boolean
}

/**
 * Ordinary least squares of y on x, with the confidence interval and
 * p-value corrected for AR(1) residuals: Santer et al. (2000) widening,
 * Nychka et al. (2000) n_eff. `adjacent[i]` marks whether point i+1
 * follows point i without a gap.
 */
export function olsTrend(x: readonly number[], y: readonly number[], adjacent?: readonly boolean[], confidence = 0.95): OlsTrend | null {
  const n = x.length
  if (n < 3 || y.length !== n) return null
  const mx = x.reduce((s, v) => s + v, 0) / n
  const my = y.reduce((s, v) => s + v, 0) / n
  let sxx = 0
  let sxy = 0
  let syy = 0
  for (let i = 0; i < n; i++) {
    sxx += (x[i] - mx) ** 2
    sxy += (x[i] - mx) * (y[i] - my)
    syy += (y[i] - my) ** 2
  }
  if (!(sxx > 0)) return null
  const slope = sxy / sxx
  const intercept = my - slope * mx
  const residuals = x.map((xi, i) => y[i] - (intercept + slope * xi))
  const sse = residuals.reduce((s, e) => s + e * e, 0)
  const r2 = syy > 0 ? 1 - sse / syy : 1
  const lag1 = lag1Autocorrelation(residuals, adjacent)
  const nEff = effectiveNNychka(n, lag1)
  const tooFewIndependent = nEff <= 3
  const df = Math.max(nEff - 2, 1e-9)
  const se = Math.sqrt(sse / (n - 2) / sxx)
  const seSlope = tooFewIndependent ? NaN : se * Math.sqrt((n - 2) / (nEff - 2))
  const tCrit = studentTQuantile(1 - (1 - confidence) / 2, df)
  const pValue = tooFewIndependent ? NaN : seSlope > 0 ? tTestP(slope / seSlope, df) : 0
  return {
    n, slope, intercept, r2, lag1, nEff, df, seSlope,
    ciLow: tooFewIndependent ? NaN : slope - tCrit * seSlope,
    ciHigh: tooFewIndependent ? NaN : slope + tCrit * seSlope,
    pValue,
    tooFewIndependent,
    lowIndependent: nEff < LOW_INDEPENDENT,
  }
}

/** Most points Mann–Kendall takes: its pairwise slopes grow as n² (4,000 points: 8 million slopes). */
export const MK_MAX_POINTS = 4000

export interface MannKendall {
  readonly n: number
  readonly s: number
  /** Kendall's tau-b (tie-adjusted, as R's Kendall package reports). */
  readonly tau: number
  /** Var(S) with the tie correction. */
  readonly varS: number
  /** The Hamed–Rao factor n/n* (≥ 1: only positive autocorrelation is used). */
  readonly correction: number
  readonly z: number
  /** Liberal for persistent series (see the module header): report beside least squares, not alone. */
  readonly pValue: number
  /** Sen's slope: the median of all pairwise slopes, per unit of x. */
  readonly sen: number
  /** Gilbert's interval with the corrected variance; shares the test's optimism for persistent series. */
  readonly senLow: number
  readonly senHigh: number
  /** For drawing: median of y − sen·x (Conover 1980). */
  readonly senIntercept: number
  /** Share of pairwise slopes that are exactly zero (ties on 8-bit data); above 0.3 Sen's slope is pulled towards 0. */
  readonly zeroSlopeShare: number
}

/**
 * The Mann–Kendall trend test with Sen's slope.
 *
 * S counts concordant minus discordant pairs, skipping pairs with equal
 * x; Var(S) has Kendall's tie correction; the Hamed & Rao (1998)
 * correction multiplies it by 1 + 2/(n(n−1)(n−2)) Σ (n−k)(n−k−1)(n−k−2) ρₖ
 * over the significant autocorrelations ρₖ (|ρₖ| > z₀.₉₇₅/√n) of the ranks
 * of the series detrended by Sen's slope, lags taken on `grid` (integer
 * time positions) when given, never below 1. Z has the continuity
 * correction. Sen's interval follows Gilbert (1987). Null for fewer than
 * 4 or more than {@link MK_MAX_POINTS} points.
 */
export function mannKendall(x: readonly number[], y: readonly number[], confidence = 0.95, grid?: readonly number[]): MannKendall | null {
  const n = x.length
  if (n < 4 || n > MK_MAX_POINTS || y.length !== n) return null
  const order = x.map((_, i) => i).sort((a, b) => x[a] - x[b])
  const xs = order.map((i) => x[i])
  const ys = order.map((i) => y[i])
  const gs = grid ? order.map((i) => grid[i]) : undefined
  let s = 0
  let zero = 0
  const slopes: number[] = []
  for (let i = 0; i < n - 1; i++) {
    for (let j = i + 1; j < n; j++) {
      const dx = xs[j] - xs[i]
      if (dx === 0) continue
      const dy = ys[j] - ys[i]
      s += dy > 0 ? 1 : dy < 0 ? -1 : 0
      const slope = dy / dx
      if (slope === 0) zero++
      slopes.push(slope)
    }
  }
  const tieGroups = (values: readonly number[]) => {
    const counts = new Map<number, number>()
    for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1)
    return [...counts.values()].filter((c) => c > 1)
  }
  const yTies = tieGroups(ys)
  const xTies = tieGroups(xs)
  let tieTerm = 0
  for (const tc of yTies) tieTerm += tc * (tc - 1) * (2 * tc + 5)
  const varS = (n * (n - 1) * (2 * n + 5) - tieTerm) / 18
  const n0 = (n * (n - 1)) / 2
  const n1 = yTies.reduce((acc, tc) => acc + (tc * (tc - 1)) / 2, 0)
  const n2 = xTies.reduce((acc, tc) => acc + (tc * (tc - 1)) / 2, 0)
  const tau = n0 - n1 > 0 && n0 - n2 > 0 ? s / Math.sqrt((n0 - n1) * (n0 - n2)) : 0

  slopes.sort((a, b) => a - b)
  const sen = median(slopes)
  const senIntercept = median(xs.map((xi, i) => ys[i] - sen * xi).sort((a, b) => a - b))

  // Hamed & Rao: autocorrelation of the ranks of the detrended series, lags in time.
  const r = ranks(xs.map((xi, i) => ys[i] - sen * xi))
  const bound = normalQuantile(0.975) / Math.sqrt(n)
  let sum = 0
  for (let k = 1; k < n - 2; k++) {
    const rho = autocorrelation(r, k, gs)
    if (Math.abs(rho) > bound) sum += (n - k) * (n - k - 1) * (n - k - 2) * rho
  }
  const correction = Math.max(1, 1 + (2 / (n * (n - 1) * (n - 2))) * sum)
  const sd = Math.sqrt(varS * correction)
  const z = sd > 0 ? (s > 0 ? (s - 1) / sd : s < 0 ? (s + 1) / sd : 0) : 0
  const cAlpha = normalQuantile(1 - (1 - confidence) / 2) * sd
  const m = slopes.length
  return {
    n, s, tau, varS, correction, z, pValue: normalTwoSidedP(z), sen,
    senLow: m ? orderStatistic(slopes, (m - cAlpha) / 2) : NaN,
    senHigh: m ? orderStatistic(slopes, (m + cAlpha) / 2 + 1) : NaN,
    senIntercept,
    zeroSlopeShare: m ? zero / m : 0,
  }
}

// ------------------------------------------------------------------ correlation

export interface Correlation {
  readonly r: number
  readonly n: number
  readonly nEff: number
  readonly df: number
  readonly pValue: number
  readonly tooFewIndependent: boolean
}

function correlate(a: readonly number[], b: readonly number[], adjacent?: readonly boolean[]): Correlation | null {
  const n = a.length
  if (n < 4 || b.length !== n) return null
  const ma = a.reduce((s, v) => s + v, 0) / n
  const mb = b.reduce((s, v) => s + v, 0) / n
  let sab = 0
  let saa = 0
  let sbb = 0
  for (let i = 0; i < n; i++) {
    sab += (a[i] - ma) * (b[i] - mb)
    saa += (a[i] - ma) ** 2
    sbb += (b[i] - mb) ** 2
  }
  if (!(saa > 0 && sbb > 0)) return null
  const r = Math.max(-1, Math.min(1, sab / Math.sqrt(saa * sbb)))
  // Bretherton et al. (1999): two AR(1) series share fewer independent points.
  const product = lag1Autocorrelation(a, adjacent) * lag1Autocorrelation(b, adjacent)
  const nEff = product > 0 ? Math.max(0, Math.min(n, (n * (1 - product)) / (1 + product))) : n
  const tooFewIndependent = nEff <= 3
  const df = Math.max(nEff - 2, 1e-9)
  const t = Math.abs(r) >= 1 ? Infinity : r * Math.sqrt(df / (1 - r * r))
  return { r, n, nEff, df, pValue: tooFewIndependent ? NaN : tTestP(t, df), tooFewIndependent }
}

/** Pearson's r with a significance test on the effective sample size. */
export function pearson(a: readonly number[], b: readonly number[], adjacent?: readonly boolean[]): Correlation | null {
  return correlate(a, b, adjacent)
}

/** Spearman's ρ (Pearson on average ranks), tested the same way on the ranks. */
export function spearman(a: readonly number[], b: readonly number[], adjacent?: readonly boolean[]): Correlation | null {
  return correlate(ranks(a), ranks(b), adjacent)
}

export interface LagCorrelation {
  /** Positive lag: b follows a by `lag` steps. */
  readonly lag: number
  readonly result: Correlation
}

/**
 * Pearson's r at each lag from −maxLag to +maxLag over aligned steps
 * (`null` where either side has no value). Picking the best of many lags
 * inflates significance: callers must correct for how many were tried.
 */
export function lagCorrelations(a: readonly (number | null)[], b: readonly (number | null)[], maxLag: number): LagCorrelation[] {
  const out: LagCorrelation[] = []
  for (let lag = -maxLag; lag <= maxLag; lag++) {
    const xa: number[] = []
    const xb: number[] = []
    const adjacent: boolean[] = []
    let lastIndex = -2
    for (let i = 0; i < a.length; i++) {
      const j = i + lag
      if (j < 0 || j >= b.length) continue
      const va = a[i]
      const vb = b[j]
      if (va === null || vb === null) continue
      if (xa.length) adjacent[xa.length - 1] = i === lastIndex + 1
      xa.push(va)
      xb.push(vb)
      lastIndex = i
    }
    const result = pearson(xa, xb, adjacent)
    if (result) out.push({ lag, result })
  }
  return out
}

// ------------------------------------------------------------------ time of emergence

/** Fewest baseline values for a noise estimate (an SD from 10 values is ±23 % uncertain; 20 is the floor here). */
export const MIN_BASELINE_VALUES = 20

export interface EmergenceOptions {
  /** Centred running-mean window, in the same units as the times (a span, not a count). */
  readonly windowSpan: number
  /** The regular spacing of the series (one step), to count how much of a window is present. */
  readonly step: number
  /** Standard deviations the signal must stay beyond. Default 2. */
  readonly threshold?: number
  /** Noise from elsewhere (observations, one model run); otherwise the detrended baseline's SD. */
  readonly noise?: number | null
  /** One quantisation step of the data: the noise is never taken smaller than this. */
  readonly quantum?: number | null
}

export interface Emergence {
  /** The baseline period's mean, and the noise used (detrended SD, floored at the quantum, or the given value). */
  readonly baseMean: number
  readonly baseSd: number
  readonly baseN: number
  readonly noiseFrom: 'baseline' | 'given' | 'quantum'
  /** The signal-to-noise at each point (NaN where the centred window is not inside the record or under 70 % present). */
  readonly signalToNoise: readonly number[]
  /** The time from which |S/N| stays at or above the threshold, with one sign, to the last point that can be smoothed; null: not emerged. */
  readonly emergedAt: number | null
  readonly direction: 1 | -1 | 0
  readonly threshold: number
  /** The last time the check could see (the record's end minus half a window). */
  readonly checkedTo: number | null
}

/**
 * Time of emergence (Hawkins & Sutton 2012). Noise: the standard deviation
 * of the baseline period's values about their own least-squares line (a
 * trend inside the baseline is signal, not noise), or `noise` when given,
 * never below one quantisation step. Signal: a centred running mean over
 * `windowSpan` of time, computed only where the whole window lies inside
 * the record and at least 70 % of its steps are present. Emergence: the
 * first time after the baseline period from which |signal − baseline
 * mean| / noise stays at or above the threshold, with one sign, at every
 * later point that can be smoothed. Null when the baseline has fewer than
 * {@link MIN_BASELINE_VALUES} values or no noise to measure against.
 *
 * Retrospective by construction: a centred mean uses later values, so this
 * is not a real-time detector.
 */
export function timeOfEmergence(
  times: readonly number[],
  values: readonly number[],
  baseStart: number,
  baseEnd: number,
  opts: EmergenceOptions,
): Emergence | null {
  const n = times.length
  if (n !== values.length || n === 0 || !(opts.step > 0) || !(opts.windowSpan > 0)) return null
  const threshold = opts.threshold ?? 2
  const bt: number[] = []
  const bv: number[] = []
  times.forEach((t, i) => {
    if (t >= baseStart && t <= baseEnd) {
      bt.push(t)
      bv.push(values[i])
    }
  })
  if (bv.length < MIN_BASELINE_VALUES) return null
  const baseMean = bv.reduce((s, v) => s + v, 0) / bv.length
  // Detrended SD of the baseline (H&S remove the forced signal from the noise).
  const fit = olsTrend(bt.map((t) => (t - baseStart) / opts.step), bv)
  const residualSd = fit
    ? Math.sqrt(bv.reduce((s, v, i) => s + (v - (fit.intercept + fit.slope * ((bt[i] - baseStart) / opts.step))) ** 2, 0) / (bv.length - 2))
    : NaN
  let baseSd: number
  let noiseFrom: Emergence['noiseFrom']
  if (opts.noise != null && opts.noise > 0) {
    baseSd = opts.noise
    noiseFrom = 'given'
  } else {
    baseSd = residualSd
    noiseFrom = 'baseline'
  }
  if (opts.quantum != null && opts.quantum > 0 && !(baseSd >= opts.quantum)) {
    baseSd = opts.quantum
    noiseFrom = 'quantum'
  }
  if (!(baseSd > 0)) return null

  const half = opts.windowSpan / 2
  const expected = Math.round(opts.windowSpan / opts.step) + 1
  const first = times[0]
  const last = times[n - 1]
  let lo = 0
  let hi = 0
  let sum = 0
  const signalToNoise = times.map((t) => {
    // Points with |t_k − t| ≤ half, by a sliding window over sorted times.
    while (hi < n && times[hi] <= t + half + 1e-9 * opts.step) sum += values[hi++]
    while (lo < hi && times[lo] < t - half - 1e-9 * opts.step) sum -= values[lo++]
    if (t - half < first - 1e-9 * opts.step || t + half > last + 1e-9 * opts.step) return NaN
    const count = hi - lo
    return count >= Math.ceil(0.7 * expected) ? (sum / count - baseMean) / baseSd : NaN
  })
  let checkedTo: number | null = null
  for (let i = n - 1; i >= 0; i--) if (Number.isFinite(signalToNoise[i])) { checkedTo = times[i]; break }

  let emergedAt: number | null = null
  let direction: 1 | -1 | 0 = 0
  for (let i = n - 1; i >= 0; i--) {
    const sn = signalToNoise[i]
    if (!Number.isFinite(sn)) continue
    const sign = sn >= threshold ? 1 : sn <= -threshold ? -1 : 0
    if (sign === 0 || (direction !== 0 && sign !== direction)) break
    if (times[i] <= baseEnd) break
    direction = sign
    emergedAt = times[i]
  }
  return {
    baseMean, baseSd, baseN: bv.length, noiseFrom, signalToNoise,
    emergedAt, direction: emergedAt === null ? 0 : direction, threshold, checkedTo,
  }
}
